import i18n from '../i18n';
import type { TFunction } from 'i18next';
import type { TranslationKey } from '@finance/contracts';
import { CATALOG_NAMESPACES, SUPPORTED_LOCALES, catalogHasKey } from './catalog-registry';
import type {
  ResolvedTranslationKey,
  StaticTranslator,
  TranslationOptions,
} from './static-translator';

const KNOWN_NAMESPACES = new Set(CATALOG_NAMESPACES);
export const SAFE_I18N_FALLBACK_KEY: TranslationKey = 'common.i18n.contentUnavailable';

function defaultNamespace(): string {
  return (i18n.options?.defaultNS as string | undefined) ?? 'common';
}

function stripDefaultNamespace(key: string): string | undefined {
  const defaultNS = defaultNamespace();
  if (key.includes(':')) {
    const [ns, ...rest] = key.split(':');
    if (ns === defaultNS) return rest.join(':');
    return undefined;
  }
  const dotIndex = key.indexOf('.');
  if (dotIndex <= 0) return undefined;
  const ns = key.slice(0, dotIndex);
  if (ns === defaultNS) return key.slice(dotIndex + 1);
  return undefined;
}

function resolveRawKey(key: string): ResolvedTranslationKey {
  if (!key) return key as ResolvedTranslationKey;
  const stripped = stripDefaultNamespace(key);
  if (stripped !== undefined) return stripped as ResolvedTranslationKey;
  if (key.includes(':')) return key as ResolvedTranslationKey;
  const dotIndex = key.indexOf('.');
  if (dotIndex <= 0) return key as ResolvedTranslationKey;
  const namespace = key.slice(0, dotIndex);
  return (KNOWN_NAMESPACES.has(namespace)
    ? `${namespace}:${key.slice(dotIndex + 1)}`
    : key) as ResolvedTranslationKey;
}

/**
 * Resolve a static or descriptor-provided key for i18next. Keys are checked
 * against the compiled catalog and resolved when known. Unknown keys are
 * still passed through so i18next can attempt translation (e.g. from a
 * custom resource bundle); the raw-key detector is the final safety net
 * that prevents untranslated keys from reaching user-visible presentation.
 */
export function resolveTKey(key: TranslationKey | string): ResolvedTranslationKey {
  return resolveRawKey(key);
}

/** True only when a runtime value is a known compiled translation key. */
export function isRuntimeTranslationKey(key: string | undefined | null): key is string {
  return Boolean(key && catalogHasKey(SUPPORTED_LOCALES.en.catalog, key));
}

/**
 * Resolve a runtime value only after proving catalog membership. Use this when
 * deciding whether an arbitrary string is business data or a translation key.
 */
export function resolveRuntimeTKey(key: string | undefined | null): ResolvedTranslationKey | undefined {
  if (!isRuntimeTranslationKey(key)) return undefined;
  return resolveRawKey(key);
}

/**
 * Translate a value that is semantically declared to be a translation key.
 * Unknown non-empty keys render a catalog-owned safe fallback, never the key.
 * Absence remains undefined so optional presentation fields can stay absent.
 */
export function translateRuntimeTKey(
  t: TFunction | StaticTranslator,
  key: string | undefined | null,
  options?: TranslationOptions,
): string | undefined {
  if (!key) return undefined;
  const resolved = resolveRuntimeTKey(key) ?? resolveTKey(SAFE_I18N_FALLBACK_KEY);
  return String(t(resolved, options));
}

/** Translate a required presentation key with an explicitly chosen fallback. */
export function translateRuntimeTKeyOrFallback(
  t: TFunction | StaticTranslator,
  key: string | undefined | null,
  options?: TranslationOptions,
  fallbackKey: TranslationKey = SAFE_I18N_FALLBACK_KEY,
): string {
  const resolved = resolveRuntimeTKey(key) ?? resolveTKey(fallbackKey);
  return String(t(resolved, options));
}
